import type { Post } from '@entities/post'
import { EDisplayPosts } from '@pages/scheduled-posts-page/ui/scheduled-posts-page'
import SmallPreviewPost, { PreviewPostText } from '@shared/ui/small-preview-post/small-preview-post'
import { Col, Radio, Row, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useState } from 'react'
import { CustomTag } from '../custom-tag/custom-tag'
import './weekly-calendar.scss'
const { Title } = Typography

dayjs.extend(isoWeek)

interface TableRow {
    key: string | number
    time: string
    [date: string]: Post[] | string | number | undefined
}

interface IWeeklyCalendar {
    posts?: Post[]
    onChange: ({ startDate, endDate }: { startDate: Date; endDate: Date }) => void
    onClick: (post: Post) => void
    setDisplay: (display: EDisplayPosts) => void
}

const WeeklyCalendar = ({ posts, onChange, onClick, setDisplay }: IWeeklyCalendar) => {
    const [currentDate, setCurrentDate] = useState(dayjs())
    if (!posts) {
        return null
    }

    const getWeekDates = (startDate: Dayjs) => {
        const startOfWeek = startDate.startOf('isoWeek')
        return Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day').format('DD.MM dd'))
    }

    const weekDates = getWeekDates(currentDate)

    const expandedData: TableRow[] = []
    const eventsGroupedByTime: Record<string, TableRow> = {}

    const allEvents = posts
        .filter((event) => weekDates.includes(dayjs(event.publishDate).format('DD.MM dd')))
        .sort((a, b) => {
            const timeA = dayjs(a.publishDate).hour() * 60 + dayjs(a.publishDate).minute()
            const timeB = dayjs(b.publishDate).hour() * 60 + dayjs(b.publishDate).minute()
            return timeA - timeB
        })

    allEvents.forEach((event) => {
        const eventTime = dayjs(event.publishDate).format('HH:mm')
        const eventDate = dayjs(event.publishDate).format('DD.MM dd')

        if (!eventsGroupedByTime[eventTime]) {
            eventsGroupedByTime[eventTime] = {
                key: eventTime,
                time: eventTime
            }
        }

        if (!eventsGroupedByTime[eventTime][eventDate]) {
            eventsGroupedByTime[eventTime][eventDate] = []
        }

        ;(eventsGroupedByTime[eventTime][eventDate] as Post[]).push(event)
    })

    for (const time in eventsGroupedByTime) {
        expandedData.push(eventsGroupedByTime[time])
    }

    const handleClick = (event: Post) => {
        onClick(event)
    }

    const columns: ColumnsType<TableRow> = [
        {
            title: 'Таймслот',
            dataIndex: 'time',
            key: 'time',
            width: 100,
            align: 'center'
        },
        ...weekDates.map((date) => ({
            title: date,
            dataIndex: date,
            key: date,
            width: 150,
            onHeaderCell: () => ({
                onClick: () => {
                    const selectedDate = dayjs(date, 'DD.MM')
                    onChange({
                        startDate: selectedDate.startOf('day').toDate(),
                        endDate: selectedDate.endOf('day').toDate()
                    })
                    setDisplay(EDisplayPosts.day)
                },
                style: { cursor: 'pointer' }
            }),
            render: (events: Post[]) => {
                return events?.length > 0
                    ? events.map((event: Post) => {
                          return (
                              <CustomTag
                                  styleTag={{
                                      cursor: 'pointer',
                                      width: 150
                                  }}
                                  color={event.postType?.color}
                                  key={event.id}
                                  onClick={() => handleClick(event)}
                                  container
                              >
                                  <Space>
                                      <SmallPreviewPost post={event} />
                                      <PreviewPostText post={event} />
                                  </Space>
                              </CustomTag>
                          )
                      })
                    : null
            }
        }))
    ]

    const handlePrevWeek = () => {
        onChange({
            startDate: dayjs(currentDate).subtract(1, 'week').startOf('week').toDate(),
            endDate: dayjs(currentDate).subtract(1, 'week').endOf('week').toDate()
        })

        setCurrentDate((prevDate) => prevDate.subtract(1, 'week'))
    }

    const handleNextWeek = () => {
        onChange({
            startDate: dayjs(currentDate).add(1, 'week').startOf('week').toDate(),
            endDate: dayjs(currentDate).add(1, 'week').endOf('week').toDate()
        })

        setCurrentDate((prevDate) => prevDate.add(1, 'week'))
    }

    return (
        <div>
            <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
                <Radio.Group>
                    <Radio.Button onClick={handlePrevWeek}>Назад</Radio.Button>
                    <Radio.Button onClick={handleNextWeek}>Вперед</Radio.Button>
                </Radio.Group>
                <Col flex="auto" style={{ textAlign: 'center', marginRight: 100 }}>
                    <Title level={5}>
                        Неделя: {currentDate.startOf('isoWeek').format('DD.MM')} -{' '}
                        {currentDate.endOf('isoWeek').format('DD.MM.YYYY')}
                    </Title>
                </Col>
                <div />
            </Row>
            <Table
                rowClassName={'custom-table'}
                columns={columns}
                dataSource={expandedData}
                pagination={false}
                bordered
                scroll={{ x: '100%', y: 'calc(100vh - 500px)' }}
            />
        </div>
    )
}

export default WeeklyCalendar
